import { AlkLogger, uuidv4 } from '@alk/core';
import { ExtraDigitSize } from '@fnx/fx-pad/dist/types/interfaces/prices.interface';
import { ForwardPriceRequestWithClient, IForwardQuote, ISessionDisposable } from '../../../interfaces';
import { IForwardStreamAdapterService } from './forward-stream-adapter.interface';
import { injectable } from 'inversify';
import { IPoint, IPrice } from '@fnx/fx-pad';
import { map, take } from 'rxjs/operators';
import { Observable, OperatorFunction, timer } from 'rxjs';

const INTERVAL_BETWEEN_QUOTE = 1_000;
const MAX_STARTING_DELAY = 5_000;
const MAX_NUMBER_OF_QUOTES = 50;

type TInstrumentSetttings = { points: number, price: number, extraDigits: ExtraDigitSize, fraction: number };

const initialByInstrument: Record<string, TInstrumentSetttings> = {
    'EURUSD': { points: 12.2, price: 1.35339, extraDigits: 1, fraction: 4 },
    'EURGBP': { points: -30000.415, price: 1.35339, extraDigits: 3, fraction: 4 },
    'USDJPY': { points: 300.41, price: 1.35, extraDigits: 2, fraction: 2 },
};

const DEFAULT_INSTRUMENT_SETTINGS: TInstrumentSetttings =
    { points: 12.2, price: 1.35339, extraDigits: 2, fraction: 4 };

/**
 * We want a coef that is between 0.995 and 1.005 to simulate price fluctuation.
 */
const randomizePrice = (instrument: string): IPrice => {

    const { price, fraction: FractionSize, extraDigits: ExtraDigits } =
        initialByInstrument[ instrument ] ?? DEFAULT_INSTRUMENT_SETTINGS;

    return {
        Value: price * (1 + (Math.random() - 0.5) / 100),
        ExtraDigits,
        FractionSize,
    };
};

const randomizePoint = (instrument: string): IPoint => {

    const { points, extraDigits: ExtraDigits } =
        initialByInstrument[ instrument ] ?? DEFAULT_INSTRUMENT_SETTINGS;

    return {
        Value: points * (1 + (Math.random() - 0.5) / 100),
        ExtraDigits,
    };
};

const buildRandomForwardQuoteOperator =
    (request: ForwardPriceRequestWithClient) =>
        (subId: number): OperatorFunction<number, IForwardQuote> =>
            map(() => ({
                requestId: subId,
                productType: 'Forward',
                askPrice: randomizePrice(request.ccyPair),
                bidPrice: randomizePrice(request.ccyPair),
                askPointsNear: randomizePoint(request.ccyPair),
                bidPointsNear: randomizePoint(request.ccyPair),
                spotAskPrice: randomizePrice(request.ccyPair),
                spotBidPrice: randomizePrice(request.ccyPair),
                askPointsFar: randomizePoint(request.ccyPair),
                askPriceFar: randomizePrice(request.ccyPair),
                askSwapRate: randomizePoint(request.ccyPair),
                bidPointsFar: randomizePoint(request.ccyPair),
                bidPriceFar: randomizePrice(request.ccyPair),
                bidSwapRate: randomizePoint(request.ccyPair),
                spotSpread: randomizePoint(request.ccyPair),
                forwardSpread: randomizePoint(request.ccyPair),
                swapSpread: randomizePoint(request.ccyPair),
                swapMidRate: randomizePrice(request.ccyPair),
                quoteId: uuidv4(),
            }));

@injectable()
export class ForwardStreamAdapterMock implements IForwardStreamAdapterService, ISessionDisposable {

    private subId = Math.floor(Math.random() * 100) * 1000;

    priceObservable(request: ForwardPriceRequestWithClient): Observable<IForwardQuote> {
        AlkLogger.debug(`[ForwardStreamAdapterMock#priceObservable] asked for ${ JSON.stringify(request) }`);
        const requestId = this.subId++;
        const randomStartingDelay = MAX_STARTING_DELAY * Math.random();

        return timer(randomStartingDelay, INTERVAL_BETWEEN_QUOTE)
            .pipe(
                take(MAX_NUMBER_OF_QUOTES),
                map(nb => {
                    if (Math.random() > 0.95) {
                        throw new Error('simulated quote error');
                    }

                    return nb;
                }),
                buildRandomForwardQuoteOperator(request)(requestId),
            );
    }

    stop: () => void;
}
